import { useCallback, useEffect, useRef, useState } from 'react';
import Image from '../common/Image';
import './ProductImageGallery.css';

const ZOOM = 1.45;
const TILT_DEG = 4;
// Keeps the panned image inside the frame once the tilt foreshortens its edges.
const PAN_LIMIT = 0.9;
// Fraction of the remaining distance covered per frame. Higher follows the
// cursor more tightly, lower feels heavier.
const EASE = 0.16;
const EPSILON = 0.002;

const REST = { x: 0, y: 0, rx: 0, ry: 0, s: 1 };
const KEYS = ['x', 'y', 'rx', 'ry', 's'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ProductImageGallery = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(0);

  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const rafRef = useRef(null);
  const target = useRef({ ...REST });
  const current = useRef({ ...REST });

  // The transform is written straight to the DOM inside an animation frame.
  // Routing it through state would re-render the gallery on every pointer move,
  // and a CSS transition would restart on every move instead of tracking.
  const step = useCallback(() => {
    const from = current.current;
    const to = target.current;
    let settled = true;

    KEYS.forEach(key => {
      const delta = to[key] - from[key];
      if (Math.abs(delta) > EPSILON) {
        from[key] += delta * EASE;
        settled = false;
      } else {
        from[key] = to[key];
      }
    });

    const stage = stageRef.current;
    if (stage) {
      stage.style.transform =
        `perspective(900px) ` +
        `translate3d(${from.x.toFixed(2)}px, ${from.y.toFixed(2)}px, 0) ` +
        `rotateX(${from.rx.toFixed(3)}deg) rotateY(${from.ry.toFixed(3)}deg) ` +
        `scale(${from.s.toFixed(4)})`;
    }

    rafRef.current = settled ? null : requestAnimationFrame(step);
  }, []);

  const start = useCallback(() => {
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(step);
    }
  }, [step]);

  useEffect(
    () => () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    },
    []
  );

  const rest = useCallback(() => {
    target.current = { ...REST };
    start();
  }, [start]);

  const track = useCallback(
    event => {
      if (event.pointerType === 'touch' || prefersReducedMotion()) return;

      const frame = frameRef.current;
      if (!frame) return;

      const { left, top, width, height } = frame.getBoundingClientRect();
      if (!width || !height) return;

      const halfWidth = width / 2;
      const halfHeight = height / 2;
      const nx = clamp((event.clientX - left - halfWidth) / halfWidth, -1, 1);
      const ny = clamp((event.clientY - top - halfHeight) / halfHeight, -1, 1);

      // (1 - ZOOM) is negative, so the image slides against the cursor and the
      // point under it stays put - a magnifier, not a drift.
      target.current = {
        x: nx * halfWidth * (1 - ZOOM) * PAN_LIMIT,
        y: ny * halfHeight * (1 - ZOOM) * PAN_LIMIT,
        rx: -ny * TILT_DEG,
        ry: nx * TILT_DEG,
        s: ZOOM,
      };
      start();
    },
    [start]
  );

  if (!images || images.length === 0) {
    return (
      <div className="product-image-gallery">
        <div className="main-image">
          <div className="main-image__stage">
            <Image src={null} alt="Product" fallback="/placeholder-watch.jpg" />
          </div>
        </div>
      </div>
    );
  }

  const sortedImages = [...images].sort((a, b) => {
    // Sort by is_primary first, then by display_order
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return (a.display_order || 0) - (b.display_order || 0);
  });

  const mainImageUrl =
    sortedImages[selectedImage]?.image_url || sortedImages[0]?.image_url;

  return (
    <div className="product-image-gallery">
      {sortedImages.length > 1 && (
        <div className="thumbnail-images">
          {sortedImages.map((image, index) => (
            <button
              key={image.id}
              className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
              onClick={() => setSelectedImage(index)}
            >
              <Image
                src={image.image_url}
                alt={`Product ${index + 1}`}
                fallback="/placeholder-watch.jpg"
              />
            </button>
          ))}
        </div>
      )}
      <div
        className="main-image"
        ref={frameRef}
        onPointerEnter={track}
        onPointerMove={track}
        onPointerLeave={rest}
        onPointerCancel={rest}
      >
        <div className="main-image__stage" ref={stageRef}>
          <Image
            src={mainImageUrl}
            alt="Product"
            fallback="/placeholder-watch.jpg"
          />
        </div>
      </div>
    </div>
  );
};

export default ProductImageGallery;
